/**
 * LayoutManager.ts - UI Workspace and Layout Persistence Service
 *
 * Manages persisting, loading, and resetting UI layout configurations:
 * - Drawer Position ('bottom' | 'right')
 * - Drawer Resized Height
 * - Floating Legend (repositionable coordinates x,y, or smart anchor)
 * - Legend Collapsed State
 * - Master Timeline Horizon (5-Day vs 10-Day)
 *
 * Persists locally to localStorage and remotely to Cloudflare KV via POST /api/user/layout
 */

import store, { UserUiLayout } from '../state';
import authService from './AuthService';

export class LayoutManager {
  private static STORAGE_KEY = 'gw_ui_layout';

  /**
   * Captures the current active UI layout snapshot from DOM and state
   */
  public static getCurrentLayout(overlayEl?: HTMLElement | null): UserUiLayout {
    const state = store.getState();
    const overlay = overlayEl || document.getElementById('map-legend-overlay');

    let legendPos: { x: number; y: number } | null = null;
    if (overlay && (overlay as any)._isCustomPositioned) {
      const rect = overlay.getBoundingClientRect();
      legendPos = { x: Math.round(rect.left), y: Math.round(rect.top) };
    }

    const isLegendCollapsed = !!(overlay as any)?._collapsed;
    const rightDrawerH = parseInt(localStorage.getItem('gw_right_drawer_height') || '600', 10);
    const savedLegendW = parseInt(localStorage.getItem('skyskout_legend_width') || '0', 10);
    const savedLegendH = parseInt(localStorage.getItem('skyskout_legend_height') || '0', 10);

    return {
      drawerPosition: state.drawerPosition || 'bottom',
      drawerHeight: isNaN(rightDrawerH) ? 600 : rightDrawerH,
      legendPosition: legendPos,
      legendWidth: savedLegendW > 0 ? savedLegendW : undefined,
      legendHeight: savedLegendH > 0 ? savedLegendH : undefined,
      isLegendCollapsed,
      timelineHorizon: state.timelineHorizon || 10,
      uiFontSize: state.uiFontSize || 'compact',
      units: state.units || (localStorage.getItem('skyskout_units') as any) || 'imperial',
      updatedAt: Date.now()
    };
  }

  /**
   * Persists layout data to localStorage and remote API (if authenticated)
   */
  public static async saveLayout(customLayout?: Partial<UserUiLayout>, overlayEl?: HTMLElement | null): Promise<boolean> {
    const current = this.getCurrentLayout(overlayEl);
    const layoutData: UserUiLayout = {
      ...current,
      ...(customLayout || {}),
      updatedAt: Date.now()
    };

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(layoutData));
      store.update({ savedUiLayout: layoutData, isUiLayoutDirty: false });

      const state = store.getState();
      if (state.isAuthenticated && (authService as any).auth?.currentUser) {
        const user = (authService as any).auth.currentUser;
        const idToken = await user.getIdToken(false);
        const apiBase = authService.getApiBase();
        if (apiBase) {
          await fetch(`${apiBase}/api/user/layout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(layoutData)
          });
        }
      }
      return true;
    } catch (err) {
      console.warn('Failed to save UI layout:', err);
      return false;
    }
  }

  /**
   * Reads saved layout from localStorage
   */
  public static loadSavedLayout(): UserUiLayout | null {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.drawerPosition === 'bottom' || parsed.drawerPosition === 'right')) {
        return parsed as UserUiLayout;
      }
    } catch (_) {}
    return null;
  }

  /**
   * Applies a layout structure to active DOM and application store
   */
  public static applyLayout(layout: UserUiLayout, overlayEl?: HTMLElement | null): void {
    const overlay = overlayEl || document.getElementById('map-legend-overlay');

    if (layout.drawerHeight) {
      try {
        localStorage.setItem('gw_right_drawer_height', String(layout.drawerHeight));
      } catch (_) {}
    }

    if (overlay) {
      if (layout.legendPosition && typeof layout.legendPosition.x === 'number' && typeof layout.legendPosition.y === 'number') {
        const clampedX = Math.max(8, Math.min(window.innerWidth - 100, layout.legendPosition.x));
        const clampedY = Math.max(48, Math.min(window.innerHeight - 100, layout.legendPosition.y));
        overlay.style.left = `${clampedX}px`;
        overlay.style.top = `${clampedY}px`;
        overlay.style.right = 'auto';
        (overlay as any)._isCustomPositioned = true;
      } else {
        (overlay as any)._isCustomPositioned = false;
      }

      if (layout.isLegendCollapsed !== undefined) {
        (overlay as any)._collapsed = layout.isLegendCollapsed;
      }

      if (layout.legendWidth) {
        overlay.style.width = `${layout.legendWidth}px`;
        try {
          localStorage.setItem('skyskout_legend_width', String(layout.legendWidth));
        } catch (_) {}
      }

      if (layout.legendHeight) {
        overlay.style.height = `${layout.legendHeight}px`;
        (overlay as any)._isCustomHeight = true;
        try {
          localStorage.setItem('skyskout_legend_height', String(layout.legendHeight));
        } catch (_) {}
      }
    }

    if (layout.uiFontSize) {
      try {
        localStorage.setItem('gw_ui_font_size', layout.uiFontSize);
        document.documentElement.setAttribute('data-ui-font-size', layout.uiFontSize);
      } catch (_) {}
    }

    if (layout.units) {
      try {
        localStorage.setItem('skyskout_units', layout.units);
      } catch (_) {}
    }

    store.update({
      drawerPosition: layout.drawerPosition,
      timelineHorizon: layout.timelineHorizon || 10,
      uiFontSize: layout.uiFontSize || store.getState().uiFontSize || 'compact',
      units: layout.units || store.getState().units || 'imperial',
      savedUiLayout: layout,
      isUiLayoutDirty: false
    });
  }

  /**
   * Resets UI layout back to default settings
   */
  public static async resetDefaultLayout(overlayEl?: HTMLElement | null): Promise<void> {
    const overlay = overlayEl || document.getElementById('map-legend-overlay');
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem('gw_drawer_position');
      localStorage.removeItem('gw_right_drawer_height');
      localStorage.removeItem('skyskout_legend_width');
      localStorage.removeItem('skyskout_legend_height');
      localStorage.removeItem('gw_ui_font_size');
      localStorage.removeItem('skyskout_units');
    } catch (_) {}

    if (overlay) {
      overlay.style.removeProperty('left');
      overlay.style.removeProperty('top');
      overlay.style.removeProperty('right');
      overlay.style.removeProperty('width');
      overlay.style.removeProperty('height');
      (overlay as any)._isCustomPositioned = false;
      (overlay as any)._isCustomHeight = false;
      (overlay as any)._collapsed = false;
      (overlay as any)._lastLegendSig = '';
    }

    try {
      document.documentElement.setAttribute('data-ui-font-size', 'compact');
    } catch (_) {}

    store.update({
      drawerPosition: 'bottom',
      timelineHorizon: 10,
      uiFontSize: 'compact',
      units: 'imperial',
      savedUiLayout: null,
      isUiLayoutDirty: false
    });

    const state = store.getState();
    if (state.isAuthenticated && (authService as any).auth?.currentUser) {
      try {
        const user = (authService as any).auth.currentUser;
        const idToken = await user.getIdToken(false);
        const apiBase = authService.getApiBase();
        if (apiBase) {
          await fetch(`${apiBase}/api/user/layout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
              drawerPosition: 'bottom',
              drawerHeight: 600,
              legendPosition: null,
              isLegendCollapsed: false,
              timelineHorizon: 10
            })
          });
        }
      } catch (err) {
        console.warn('Failed to reset remote layout:', err);
      }
    }
  }
}

export default LayoutManager;
